/*
  Carnevore Mountain homepage behaviour.
  - <cm-delivery-check>: the postal code check. It looks up the longest matching postal code
    prefix in the delivery schedule, names the delivery day or days, lights up the matching days,
    shows the maps for that postal code (its region close-up and region map, then a map for each
    delivery day, with a button for each), and announces the result to screen readers.
  - Reveal: sections below the fold fade and rise into place once, when scrolled to.
    Nothing is hidden without JavaScript, and nothing moves when reduced motion is on.
  - Links to #check scroll to the checker and put the cursor in the postal code field.
  Custom elements set themselves up again when a section reloads in the theme editor.
*/
(() => {
  if (window.cmHome) return;
  window.cmHome = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Delivery day check ---------- */
  class CmDeliveryCheck extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.schedule = this.readJson('[data-schedule]', {});
      this.regions = this.readJson('[data-regions]', []).map((region) => ({
        id: region.id,
        prefixes: CmDeliveryCheck.prefixes(region.prefixes),
      }));
      this.form = this.querySelector('[data-form]');
      this.input = this.querySelector('[data-input]');
      this.result = this.querySelector('[data-result]');
      this.resultText = this.querySelector('[data-result-text]');
      this.resultIcon = this.querySelector('[data-result-icon]');
      this.caption = this.querySelector('[data-caption]');
      this.captionDot = this.querySelector('[data-caption-dot]');
      this.captionText = this.querySelector('[data-caption-text]');
      this.frame = this.querySelector('.cm-zone__frame');
      this.tabs = this.querySelector('[data-tabs]');
      this.maps = {};
      this.querySelectorAll('template[data-map]').forEach((template) => {
        this.maps[template.dataset.map] = template;
      });
      this.activeMap = '__default__';
      this.days = [];
      this.zones = {};
      this.querySelectorAll('[data-zone]').forEach((pill) => {
        this.zones[pill.dataset.zone] = { label: pill.dataset.label, color: pill.dataset.color, pill };
      });
      if (!this.form || !this.input || !this.result) return;

      this.onSubmit = (event) => {
        event.preventDefault();
        this.check();
      };
      this.onInput = () => this.tidy();
      this.onTab = (event) => {
        const tab = event.target.closest('[data-tab]');
        if (tab) this.activate(tab.dataset.tab);
      };
      this.form.addEventListener('submit', this.onSubmit);
      this.input.addEventListener('input', this.onInput);
      if (this.tabs) this.tabs.addEventListener('click', this.onTab);
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.form.removeEventListener('submit', this.onSubmit);
      this.input.removeEventListener('input', this.onInput);
      if (this.tabs) this.tabs.removeEventListener('click', this.onTab);
      this.ready = false;
    }

    readJson(selector, fallback) {
      const node = this.querySelector(selector);
      try {
        return (node && JSON.parse(node.textContent)) || fallback;
      } catch (error) {
        return fallback;
      }
    }

    // A region's postal codes as typed in the theme editor ("M, L4T L9T"), cleaned the same way as the field.
    static prefixes(value) {
      return String(value || '')
        .toUpperCase()
        .split(/[^A-Z0-9]+/)
        .filter(Boolean);
    }

    // Capitals, letters and numbers only, at most six characters.
    static clean(value) {
      return String(value || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 6);
    }

    // Shows the code the way it's written on an envelope (M5V 2T6) and keeps the cursor at the end.
    tidy() {
      const clean = CmDeliveryCheck.clean(this.input.value);
      const shown = clean.length > 3 ? `${clean.slice(0, 3)} ${clean.slice(3)}` : clean;
      if (this.input.value === shown) return;
      const atEnd = this.input.selectionStart === this.input.value.length;
      this.input.value = shown;
      if (atEnd) this.input.setSelectionRange(shown.length, shown.length);
    }

    // Longest prefix first: a full six-character code, then shorter prefixes down to two characters.
    lookup(clean) {
      for (let length = Math.min(6, clean.length); length >= 2; length -= 1) {
        const key = clean.slice(0, length);
        if (Object.prototype.hasOwnProperty.call(this.schedule, key)) return this.schedule[key];
      }
      return null;
    }

    // The region with the longest prefix that starts the code, so "L9T" beats "L9".
    regionFor(clean) {
      let best = null;
      let bestLength = 0;
      this.regions.forEach((region) => {
        region.prefixes.forEach((prefix) => {
          if (prefix.length > bestLength && clean.indexOf(prefix) === 0) {
            best = region;
            bestLength = prefix.length;
          }
        });
      });
      return best;
    }

    // The maps for a code: the region's close-up and region map, then one for each delivery day.
    mapsFor(clean, days) {
      const region = this.regionFor(clean);
      const keys = region ? [`detail-${region.id}`, `region-${region.id}`] : [];
      days.forEach((day) => keys.push(`day-${day}`));
      return keys.filter((key) => this.maps[key]);
    }

    dayList(keys) {
      const labels = keys.map((key) => (this.zones[key] && this.zones[key].label) || key);
      if (labels.length < 2) return labels.join('');
      return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
    }

    check() {
      const clean = CmDeliveryCheck.clean(this.input.value);
      if (clean.length < 3) {
        this.show('warning');
        this.light([], []);
        return;
      }
      const match = this.lookup(clean);
      if (!match) {
        this.show('error');
        this.light([], []);
        return;
      }
      if (match.mixed) {
        this.show('mixed');
        this.light([], this.mapsFor(clean, []));
        return;
      }
      const days = (Array.isArray(match.days) ? match.days : []).map((day) => String(day).toLowerCase());
      this.show('success', days, this.areaLink(clean));
      this.light(days, this.mapsFor(clean, days));
    }

    // The area page for a covered code (the section's "Area page links"), longest prefix first.
    areaLink(clean) {
      const links = this.querySelector('template[data-area-links]');
      if (!links) return null;
      const matches = Array.from(links.content.querySelectorAll('a[data-prefix]'))
        .filter((link) => link.dataset.prefix && clean.indexOf(link.dataset.prefix) === 0)
        .sort((a, b) => b.dataset.prefix.length - a.dataset.prefix.length);
      return matches.length ? matches[0] : null;
    }

    // Writes the message. It's cleared first, so the same result is announced again on a repeat check.
    show(state, days, link) {
      const message = this.querySelector(`template[data-message="${state}"]`);
      const icon = this.querySelector(`template[data-icon="${state}"]`);
      const content = document.createDocumentFragment();

      if (state === 'success' && message) {
        const [before, ...rest] = message.content.textContent.split('{days}');
        content.append(before);
        if (rest.length) {
          const strong = document.createElement('strong');
          strong.textContent = this.dayList(days);
          content.append(strong, rest.join('{days}'));
        }
        if (link) content.append(' ', link.cloneNode(true));
      } else if (message) {
        content.append(message.content.cloneNode(true));
      }

      this.resultText.textContent = '';
      this.resultIcon.textContent = '';
      this.result.dataset.state = state;
      this.result.classList.add('is-visible');
      window.requestAnimationFrame(() => {
        this.resultText.append(content);
        if (icon) this.resultIcon.append(icon.content.cloneNode(true));
      });
    }

    // Lights up the matching days and offers the code's maps, one button each, showing the first.
    // With no maps of its own, the code gets the main map, labelled with its days.
    light(days, maps) {
      this.days = days;
      Object.keys(this.zones).forEach((key) => {
        this.zones[key].pill.classList.toggle('is-active', days.includes(key));
      });

      if (this.tabs) {
        this.tabs.textContent = '';
        if (maps.length > 1) {
          maps.forEach((key) => {
            const template = this.maps[key];
            const tab = document.createElement('button');
            const dot = document.createElement('span');
            tab.type = 'button';
            tab.className = 'cm-zone__tab';
            tab.dataset.tab = key;
            tab.style.setProperty('--zone', template.dataset.color || '');
            dot.className = 'cm-zone__dot';
            dot.setAttribute('aria-hidden', 'true');
            tab.append(dot, template.dataset.label || '');
            this.tabs.append(tab);
          });
        }
        this.tabs.hidden = maps.length < 2;
      }
      this.activate(maps[0] || '__default__');
    }

    // Shows one map. A map's image is only added to the page, and so only downloaded, the first time.
    activate(key) {
      if (!this.frame) return;
      if (key !== '__default__' && !this.frame.querySelector(`[data-zone-map="${key}"]`)) {
        const template = this.maps[key];
        if (!template) return;
        const layer = document.createElement('span');
        layer.className = 'cm-zone__layer';
        layer.dataset.zoneMap = key;
        layer.append(template.content.cloneNode(true));
        this.frame.append(layer);
      }
      this.activeMap = key;
      this.frame.querySelectorAll('[data-zone-map]').forEach((layer) => {
        layer.classList.toggle('is-active', layer.dataset.zoneMap === key);
      });
      if (this.tabs) {
        this.tabs.querySelectorAll('[data-tab]').forEach((tab) => {
          tab.setAttribute('aria-pressed', String(tab.dataset.tab === key));
        });
      }

      // The new maps carry their own titles, so the label is only for the main map.
      if (!this.caption) return;
      const first = this.days[0];
      if (key === '__default__' && first && this.zones[first]) {
        this.captionDot.style.background = this.zones[first].color || '';
        this.captionText.textContent = this.dayList(this.days);
        this.caption.classList.add('is-visible');
      } else {
        this.caption.classList.remove('is-visible');
      }
    }

    focusField() {
      if (this.input) this.input.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('cm-delivery-check')) customElements.define('cm-delivery-check', CmDeliveryCheck);

  /* ---------- Links to the checker ---------- */
  // A keyboard or mouse user who follows a link to #check lands in the postal code field.
  // Touch users just scroll there, so the phone keyboard doesn't pop up uninvited.
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const focusChecker = (hash) => {
    const target = hash && document.getElementById(hash.slice(1));
    const checker = target && target.querySelector('cm-delivery-check');
    if (checker && checker.focusField) window.setTimeout(() => checker.focusField(), 60);
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href*="#"]');
    if (!link || link.pathname !== window.location.pathname || !link.hash) return;
    if (event.detail === 0 || finePointer.matches) focusChecker(link.hash);
  });

  /* ---------- Reveal on scroll ---------- */
  let observer = null;

  function reveal(root) {
    if (reducedMotion.matches || !('IntersectionObserver' in window)) return;
    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const element = entry.target;
            element.classList.add('is-in');
            observer.unobserve(element);
            // Once it has settled, hand the element back to its own hover transitions.
            window.setTimeout(() => element.classList.remove('cm-rv', 'is-in'), 1800);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
      );
    }
    root.querySelectorAll('[data-cm-reveal]').forEach((element) => {
      if (element.dataset.cmRevealSeen) return;
      element.dataset.cmRevealSeen = 'true';
      // Anything already on screen stays put; only content further down animates in.
      if (element.getBoundingClientRect().top < window.innerHeight) return;
      element.classList.add('cm-rv');
      observer.observe(element);
    });
  }

  reveal(document);
  document.addEventListener('shopify:section:load', (event) => reveal(event.target));
})();
